import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClientKind } from '@/api/client'
import {
  ATTRIBUTION_MODELS,
  type AttributionModel,
  type FirstPartyLevel,
  type FirstPartyTrafficQuery,
  type FirstPartyTrafficReport,
} from '@/api/firstPartyTraffic'
import { CHART_METRICS, type ChartMetric } from '@/components/channelBarMetric'
import { resolvePeriod, type GlobalFilters } from '@/lib/filters'

/**
 * Where the Traffic page's numbers come from. GA4 stays the default, so nothing
 * changes for a tenant until they switch; making `tmt` the default is a one-line
 * product decision here once a tenant has a month of first-party touches
 * (docs/architecture/attribution.md §9).
 */
export const TRAFFIC_SOURCES = ['ga4', 'tmt'] as const
export type TrafficSource = (typeof TRAFFIC_SOURCES)[number]
export const DEFAULT_SOURCE: TrafficSource = 'ga4'

export type { ChartMetric }

/** Blueprint 05 §5.8's default model — the Overview's channel widget has no model switch, so it always uses this one. */
export const DEFAULT_ATTRIBUTION_MODEL: AttributionModel = 'last_non_direct'

/**
 * How long a fetched level is reused: collapsing and re-expanding a row within it
 * costs no request. The filter bar and the model change the query, and so the key.
 */
export const LEVEL_STALE_MS = 5 * 60_000

/** One page of an interactive level — the API's own default page size. A "Load more" asks for the next. */
export const PAGE_SIZE = 100

/**
 * Every React Query key the first-party view reads: one per level and path, so an
 * expanded row's children are cached on their own and re-expanding costs nothing.
 * Never keyed by `limit`/`offset`, which `useInfiniteQuery` tracks as pages.
 */
export const firstPartyKeys = {
  all: ['first-party-traffic'] as const,
  report: (kind: ApiClientKind, query: FirstPartyTrafficQuery) => ['first-party-traffic', kind, query] as const,
}

/**
 * A URL-backed choice from a fixed list: an unknown value reads as the default,
 * and choosing the default removes the param, so an unmodified page keeps a plain URL.
 */
function useUrlChoice<T extends string>(param: string, options: readonly T[], fallback: T): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get(param)
  const value = (options as readonly string[]).includes(raw ?? '') ? (raw as T) : fallback
  const set = (next: T) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next === fallback) updated.delete(param)
        else updated.set(param, next)
        return updated
      },
      { replace: true },
    )
  return [value, set]
}

export function useTrafficSource(): [TrafficSource, (next: TrafficSource) => void] {
  return useUrlChoice('tr_source', TRAFFIC_SOURCES, DEFAULT_SOURCE)
}

export function useAttributionModel(): [AttributionModel, (next: AttributionModel) => void] {
  return useUrlChoice('tr_model', ATTRIBUTION_MODELS, DEFAULT_ATTRIBUTION_MODEL)
}

/** The chart metric toggle, URL-backed under `param` — `tr_chart` on the Traffic page, `ov_chart` on the Overview. */
export function useChartMetric(param = 'tr_chart'): [ChartMetric, (next: ChartMetric) => void] {
  return useUrlChoice(param, CHART_METRICS, 'revenue')
}

/**
 * The top level's query for a filter state and model — pure given `now`, so the
 * Overview's channel widget can build one outside a hook (its own fixed model, no
 * drill-down) without an implicit, untestable dependency on the current time.
 */
export function firstPartyChannelQuery(filters: GlobalFilters, model: AttributionModel, now = new Date()): FirstPartyTrafficQuery {
  const { current } = resolvePeriod(filters, now)
  return {
    from: current.from,
    to: current.to,
    compare: filters.compare === 'previous_period',
    model,
    level: 'channel',
    path: [],
    environments: filters.environments,
  }
}

/**
 * The top level's query: the global filter bar's range, comparison and
 * environments, and this page's model. Drill-down rows build theirs with `childQuery`.
 */
export function useFirstPartyQuery(filters: GlobalFilters, model: AttributionModel): FirstPartyTrafficQuery {
  return useMemo(() => firstPartyChannelQuery(filters, model), [filters, model])
}

/** One expanded row's children: the same range, model and environments, one level down, under its path. */
export function childQuery(base: FirstPartyTrafficQuery, level: FirstPartyLevel, path: readonly string[]): FirstPartyTrafficQuery {
  return { ...base, level, path: [...path], sort: undefined, limit: undefined, offset: undefined }
}

export interface FirstPartyReportResult {
  /** Every row fetched so far; the figures beside the rows are the first page's (every page repeats them). */
  data: FirstPartyTrafficReport | undefined
  isPending: boolean
  error: unknown
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  /** Why the last "load more" failed, while the rows already shown stay; null otherwise. */
  nextPageError: unknown
}

/** One level's rows, paged — shared by the top level and every drill-down, so "load more" behaves the same at each. */
export function useFirstPartyReport(query: FirstPartyTrafficQuery): FirstPartyReportResult {
  const client = useApiClient()
  const result = useInfiniteQuery({
    queryKey: firstPartyKeys.report(client.kind, query),
    queryFn: ({ pageParam }) => client.firstPartyTraffic({ ...query, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    staleTime: LEVEL_STALE_MS,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.rows.length, 0)
      return loaded < lastPage.totalRows ? loaded : undefined
    },
  })
  const pages = result.data?.pages
  return {
    data: pages?.[0] ? { ...pages[0], rows: pages.flatMap((page) => page.rows) } : undefined,
    isPending: result.isPending,
    error: result.error,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: () => void result.fetchNextPage(),
    nextPageError: result.isFetchNextPageError ? result.error : null,
  }
}
